import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

import { Globe, Shield, CheckCircle, Award, QrCode, Building2, Info, Clock, AlertTriangle, Map, Filter, X, MapPin } from 'lucide-react';
import axios from 'axios';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// UN SDG Data
import { SDG_DATA, REGIONS, TRANSLATIONS } from './essShowcaseData';

// Filter Chip Component
const FilterChip = ({ label, selected, onClick, color }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
      selected
        ? 'text-white shadow-md'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
    style={selected ? { backgroundColor: color || '#4F46E5' } : {}}
  >
    {label}
  </button>
);

// Partner Card Component with Expired styling
const PartnerCard = ({ partner, t, lang, isExpired, onSelect }) => {
  const _isRtl = lang === 'ar';

  return (
    <Card 
      className={`overflow-hidden transition-all hover:shadow-xl cursor-pointer ${
        isExpired 
          ? 'border-gray-200 opacity-60 grayscale' 
          : partner.status === 'expiring'
            ? 'border-2 border-yellow-300 hover:border-yellow-400'
            : 'border-2 border-green-200 hover:border-green-400'
      }`}
      onClick={() => onSelect && onSelect(partner)}
    >
      {/* Status Banner */}
      <div className={`px-4 py-2 text-center text-white text-sm font-medium ${
        isExpired 
          ? 'bg-gray-400'
          : partner.status === 'expiring' 
            ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
            : 'bg-gradient-to-r from-green-500 to-emerald-500'
      }`}>
        <div className="flex items-center justify-center gap-2">
          {isExpired ? <Clock className="h-4 w-4" /> : 
           partner.status === 'expiring' ? <AlertTriangle className="h-4 w-4" /> :
           <CheckCircle className="h-4 w-4" />}
          <span>
            {isExpired ? t.expiredVerification :
             partner.status === 'expiring' ? `${t.expiringSoon}` :
             t.active}
          </span>
        </div>
      </div>

      <CardHeader className="pb-2">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${isExpired ? 'bg-gray-100' : 'bg-gradient-to-br from-indigo-100 to-blue-100'}`}>
            <Building2 className={`h-6 w-6 ${isExpired ? 'text-gray-400' : 'text-indigo-600'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base truncate">{partner.name}</CardTitle>
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              <MapPin className="h-3 w-3" />
              <span>{partner.region}</span>
              <span>•</span>
              <span className="capitalize">{partner.level}</span>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 pt-0">
        {/* SDGs */}
        <div className="flex flex-wrap gap-1">
          {partner.odds?.map(sdg => (
            <span 
              key={sdg}
              className={`px-2 py-0.5 rounded-full text-white text-xs font-medium ${isExpired ? 'grayscale' : ''}`}
              style={{ backgroundColor: isExpired ? '#9ca3af' : SDG_DATA[sdg]?.color || '#666' }}
            >
              {sdg}
            </span>
          ))}
        </div>

        {/* Signature */}
        {partner.signature_short && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Shield className="h-3 w-3" />
            <span className="font-mono">{partner.signature_short}</span>
          </div>
        )}

        {/* Verify Button */}
        <Button 
          className={`w-full ${isExpired ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700'}`}
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            if (!isExpired) {
              window.open(partner.verification_url || `${BACKEND_URL}/api/public/verify/${partner.tenant_id}`, '_blank');
            }
          }}
          disabled={isExpired}
        >
          <QrCode className="h-4 w-4 mr-2" />
          {t.verifyButton}
        </Button>
      </CardContent>
    </Card>
  );
};

// Main Page Component
const ESSPartnersShowcase = () => {
  const [lang, setLang] = useState('en');
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  // Filters
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedODDs, setSelectedODDs] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  
  const t = TRANSLATIONS[lang];
  const isRtl = lang === 'ar';

  // Fetch partners
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/public/partners`);
        const data = res.data.partners || [];
        setPartners(data);
        setFilteredPartners(data);
      } catch {
        // Fallback demo data
        const demo = [
          { tenant_id: 'partner_gcc_gccpartner_6ac6c2d0', name: 'GCC Partner 01 - Housing', region: 'GCC', country: 'UAE', lat: 24.4539, lng: 54.3773, odds: [8,9,10,11,17], status: 'active', level: 'base', signature_short: 'SIG-7E22', expires_at: '2026-01-01' },
          { tenant_id: 'partner_gcc_gccpartner_d39193a0', name: 'GCC Partner 02 - Construction', region: 'GCC', country: 'UAE', lat: 25.0, lng: 55.0, odds: [8,9,10,11,17], status: 'active', level: 'base', signature_short: 'SIG-6105', expires_at: '2026-01-01' },
          { tenant_id: 'partner_africa_africapart_3557989e', name: 'Africa Partner 01 - Housing', region: 'Africa', country: 'Senegal', lat: 14.4974, lng: -14.4524, odds: [8,9,10,11,17], status: 'active', level: 'base', signature_short: 'SIG-268C', expires_at: '2026-01-01' },
          { tenant_id: 'partner_africa_africapart_51f59561', name: 'Africa Partner 02 - Agriculture', region: 'Africa', country: 'Kenya', lat: -1.2921, lng: 36.8219, odds: [8,9,10,11,17], status: 'expiring', level: 'base', signature_short: 'SIG-FC80', expires_at: '2025-02-15' },
          { tenant_id: 'partner_uk_ukpartner0_2c13af29', name: 'UK Partner 01 - Construction', region: 'UK', country: 'United Kingdom', lat: 51.5074, lng: -0.1278, odds: [8,9,10,11,17], status: 'active', level: 'base', signature_short: 'SIG-A914', expires_at: '2026-01-01' }
        ];
        setPartners(demo);
        setFilteredPartners(demo);
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, []);

  // Apply filters
  const applyFilters = useCallback(() => {
    let result = [...partners];
    
    if (selectedRegions.length > 0) {
      result = result.filter(p => selectedRegions.includes(p.region));
    }
    
    if (selectedODDs.length > 0) {
      result = result.filter(p => selectedODDs.every(odd => p.odds?.includes(odd)));
    }
    
    if (selectedStatuses.length > 0) {
      result = result.filter(p => selectedStatuses.includes(p.status));
    }
    
    setFilteredPartners(result);
  }, [partners, selectedRegions, selectedODDs, selectedStatuses]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    
    // Load Leaflet CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    
    // Load Leaflet JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => {
      if (window.L && mapRef.current && !mapInstanceRef.current) {
        const map = window.L.map(mapRef.current).setView([20, 0], 2);
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap'
        }).addTo(map);
        mapInstanceRef.current = map;
        setMapLoaded(true);
      }
    };
    document.head.appendChild(script);
    
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update map markers
  useEffect(() => {
    if (!mapInstanceRef.current || !window.L) return;
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    
    // Add new markers
    filteredPartners.forEach(partner => {
      if (partner.lat && partner.lng) {
        const isExpired = partner.status === 'expired';
        const isExpiring = partner.status === 'expiring';
        
        // Create custom icon
        const iconColor = isExpired ? '#9ca3af' : isExpiring ? '#f59e0b' : '#22c55e';
        const icon = window.L.divIcon({
          className: 'custom-marker',
          html: `<div style="background:${iconColor};width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
            <span style="color:white;font-size:10px;font-weight:bold;">${partner.odds?.length || 5}</span>
          </div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        });
        
        const marker = window.L.marker([partner.lat, partner.lng], { icon })
          .addTo(mapInstanceRef.current);
        
        // Create popup content
        const popupContent = `
          <div style="min-width:200px;font-family:system-ui;">
            <div style="font-weight:bold;font-size:14px;margin-bottom:4px;">${partner.name}</div>
            <div style="font-size:12px;color:#666;margin-bottom:8px;">
              📍 ${partner.region} ${partner.country ? `• ${partner.country}` : ''}
            </div>
            <div style="display:flex;gap:4px;flex-wrap:wrap;margin-bottom:8px;">
              ${partner.odds?.map(odd => `<span style="background:${SDG_DATA[odd]?.color || '#666'};color:white;padding:2px 6px;border-radius:4px;font-size:10px;">SDG ${odd}</span>`).join('')}
            </div>
            <div style="font-size:11px;color:#888;margin-bottom:8px;">
              🔐 ${partner.signature_short || 'N/A'}
            </div>
            <a href="${partner.verification_url || '#'}" target="_blank" style="display:inline-block;background:#4F46E5;color:white;padding:6px 12px;border-radius:6px;text-decoration:none;font-size:12px;font-weight:500;">
              Verify →
            </a>
          </div>
        `;
        
        marker.bindPopup(popupContent);
        markersRef.current.push(marker);
      }
    });
  }, [filteredPartners, mapLoaded]);

  // Toggle filter helpers
  const toggleRegion = (region) => {
    setSelectedRegions(prev => 
      prev.includes(region) ? prev.filter(r => r !== region) : [...prev, region]
    );
  };
  
  const toggleODD = (odd) => {
    setSelectedODDs(prev => 
      prev.includes(odd) ? prev.filter(o => o !== odd) : [...prev, odd]
    );
  };
  
  const toggleStatus = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };
  
  const clearFilters = () => {
    setSelectedRegions([]);
    setSelectedODDs([]);
    setSelectedStatuses([]);
  };

  const hasFilters = selectedRegions.length > 0 || selectedODDs.length > 0 || selectedStatuses.length > 0;

  return (
    <div className={`min-h-screen bg-gradient-to-b from-slate-50 to-blue-50 ${isRtl ? 'rtl' : 'ltr'}`} dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Language Selector */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        {['en', 'fr', 'ar'].map(l => (
          <Button
            key={l}
            variant={lang === l ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLang(l)}
            className={lang === l ? 'bg-indigo-600' : ''}
          >
            {l.toUpperCase()}
          </Button>
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
        
        <div className="relative container mx-auto px-4 py-20 text-center text-white">
          <BackButton label="Back" className="absolute top-4 left-4 text-white/80 hover:text-white" />
          
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Award className="h-5 w-5" />
            <span className="text-sm font-medium">ESS Partner – ODD Aligned</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.heroTitle}</h1>
          <p className="text-xl text-blue-100 mb-4">{t.heroSubtitle}</p>
          <p className="text-lg text-blue-200 max-w-3xl mx-auto">{t.heroDescription}</p>

          {/* SDG Icons Row */}
          <div className="flex justify-center gap-3 mt-8">
            {Object.entries(SDG_DATA).map(([num, data]) => (
              <div 
                key={num}
                className="w-12 h-12 rounded-xl flex items-center justify-center text-xl shadow-lg transform hover:scale-110 transition-transform cursor-pointer"
                style={{ backgroundColor: data.color }}
                title={`SDG ${num}: ${data.name}`}
                onClick={() => toggleODD(parseInt(num))}
              >
                {data.icon}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="container mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2">
            <Map className="h-6 w-6 text-indigo-600" />
            {t.mapTitle}
          </h2>
          <p className="text-gray-600">{t.mapDescription}</p>
        </div>
        
        <Card className="overflow-hidden shadow-xl">
          <div ref={mapRef} className="h-[420px] w-full" style={{ zIndex: 1 }}></div>
        </Card>
      </section>

      {/* Filters Section */}
      <section className="container mx-auto px-4 py-8">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-indigo-600" />
            <h3 className="font-semibold">{lang === 'fr' ? 'Filtres' : lang === 'ar' ? 'الفلاتر' : 'Filters'}</h3>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-gray-500">
                <X className="h-4 w-4 mr-1" />
                {t.clearFilters}
              </Button>
            )}
          </div>
          
          <div className="space-y-4">
            {/* Region Filters */}
            <div>
              <label className="text-sm text-gray-500 mb-2 block">{t.filterByRegion}</label>
              <div className="flex flex-wrap gap-2">
                {REGIONS.map(region => (
                  <FilterChip
                    key={region}
                    label={region}
                    selected={selectedRegions.includes(region)}
                    onClick={() => toggleRegion(region)}
                    color="#4F46E5"
                  />
                ))}
              </div>
            </div>
            
            {/* ODD Filters */}
            <div>
              <label className="text-sm text-gray-500 mb-2 block">{t.filterByODD}</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(SDG_DATA).map(([num, data]) => (
                  <FilterChip
                    key={num}
                    label={`${data.icon} SDG ${num}`}
                    selected={selectedODDs.includes(parseInt(num))}
                    onClick={() => toggleODD(parseInt(num))}
                    color={data.color}
                  />
                ))}
              </div>
            </div>
            
            {/* Status Filters */}
            <div>
              <label className="text-sm text-gray-500 mb-2 block">{t.filterByStatus}</label>
              <div className="flex flex-wrap gap-2">
                <FilterChip label={`✅ ${t.active}`} selected={selectedStatuses.includes('active')} onClick={() => toggleStatus('active')} color="#22c55e" />
                <FilterChip label={`⚠️ ${t.expiringSoon}`} selected={selectedStatuses.includes('expiring')} onClick={() => toggleStatus('expiring')} color="#f59e0b" />
                <FilterChip label={`❌ ${t.expired}`} selected={selectedStatuses.includes('expired')} onClick={() => toggleStatus('expired')} color="#9ca3af" />
              </div>
            </div>
          </div>
          
          {/* Results count */}
          <div className="mt-4 pt-4 border-t text-sm text-gray-500">
            {filteredPartners.length} / {partners.length} {lang === 'fr' ? 'partenaires' : lang === 'ar' ? 'شريك' : 'partners'}
          </div>
        </Card>
      </section>

      {/* Partners Grid */}
      <section className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.partnersTitle}</h2>
          <p className="text-gray-600">{t.partnersDescription}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-600"></div>
          </div>
        ) : filteredPartners.length === 0 ? (
          <div className="text-center py-12">
            <Globe className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t.noResults}</p>
            <Button variant="outline" className="mt-4" onClick={clearFilters}>
              {t.showAll}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPartners.map((partner, idx) => (
              <PartnerCard 
                key={partner.tenant_id || idx} 
                partner={partner} 
                t={t} 
                lang={lang}
                isExpired={partner.status === 'expired'}
              />
            ))}
          </div>
        )}
      </section>

      {/* Legal Notice */}
      <section className="bg-gray-100 py-12">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-100 rounded-full">
                  <Info className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t.legalTitle}</h3>
                  <p className="text-gray-600 leading-relaxed">{t.legalText}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-center gap-3 mb-4">
            {Object.entries(SDG_DATA).map(([num, data]) => (
              <span key={num} className="text-2xl" title={`SDG ${num}`}>{data.icon}</span>
            ))}
          </div>
          <p className="text-gray-400 text-sm">ESS Partner – ODD Aligned Program</p>
          <p className="text-gray-500 text-xs mt-2">Decision-support only. No financial service.</p>
        </div>
      </footer>
    </div>
  );
};

export default ESSPartnersShowcase;
